import { takeEvery, put, call, select } from "redux-saga/effects";
import endpoints, { endpointTypes } from "../../service";
import { fetchAPI } from "../../utils/service";
import * as actionTypes from "./actionTypes";
import * as actionCreators from "./actionCreators";
import * as thankyouAction from "../MultipleWarrantiesThankYou/actionCreators";
import { SUCCESS_STATUS } from "./constant";
import * as GlobalAction from "../../globalStore/actions";
import { RootState } from "../../rootReducer";
import { getPersistData, parseResponseData, setPersistData } from "../../utils/utils";
import { PersistData } from "../../middleware/persistDataMiddleware";

const myCartRoute = endpoints[endpointTypes.MY_CART];

export function* getCartItems(action) {
    const endpoint = myCartRoute.endpoints.getMyCartItems(action.payload.cartId);

    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && response.data.isSuccess) {
            yield put(actionCreators.getCartItemsSuccess({ data: response.data }));
        } else {
            const errorMsg = response.data ? response.data.error : null;
            yield put(GlobalAction.showGlobalError({ Message: errorMsg }));
            yield put(actionCreators.getCartItemsFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.getCartItemsFailure(err));
    }
}

export function* getActiveShoppingCart() {
    const endpoint = myCartRoute.endpoints.getActiveShoppingCart();

    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && response.data.isSuccess) {
            yield put(actionCreators.getActiveShoppingCartSuccess({ data: response.data }));
        } else {
            const errorMsg = response.data ? response.data.error : null;
            yield put(GlobalAction.showGlobalError({ Message: errorMsg }));
            yield put(actionCreators.getActiveShoppingCartFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.getActiveShoppingCartFailure(err));
    }
}

const cartRequestPayload = (data) => {
    const lineItem: any = {
        warrantyId: data.warrantyId,
    };
    if (data.TotatlWarrantyCost >= 0 && !!data.Currency) {
        lineItem.TotatlWarrantyCost = data.TotatlWarrantyCost;
        lineItem.ActualWarrantyCost = data.ActualWarrantyCost;
        lineItem.Currency = data.Currency;
    }
    return {
        status: 1,
        shoppingCartId: !!data.cartId ? data.cartId : 0,
        cartLineItem: [lineItem],
    };
};

const getSuccessMsg = (lineItem, action) => {
    const { addressLine1, city, stateOrProvince, zipOrPostal, country } = lineItem.buildingAddress;
    return `${addressLine1}, ${city}, ${stateOrProvince}, ${zipOrPostal}, ${country} ${SUCCESS_STATUS[action]}`;
};

export function* createShoppingCart(action) {
    const payload = cartRequestPayload(action.payload);
    const endpoint = myCartRoute.endpoints.createShoppingCart(payload);

    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && response.data.isSuccess) {
            const successMsg = getSuccessMsg(response.data.shoppingCartLineItem[0], "ADD");
            yield put(actionCreators.createShoppingCartSuccess({ data: response.data, successMsg }));
        } else {
            const errorMsg = response.data ? response.data.error : null;
            yield put(GlobalAction.showGlobalError({ Message: errorMsg }));
            yield put(actionCreators.createShoppingCartFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.createShoppingCartFailure(err));
    }
}

export function* addCartItem(action) {
    const payload = cartRequestPayload(action.payload);
    const endpoint = myCartRoute.endpoints.addCartItem(payload);

    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && response.data.isSuccess) {
            const addedItem = response.data.shoppingCartLineItem.find(
                (item) => item.warrantyId === action.payload.warrantyId
            );
            const successMsg = getSuccessMsg(addedItem, "ADD");
            yield put(actionCreators.addCartItemSuccess({ data: response.data, successMsg }));
        } else {
            const errorMsg = response.data ? response.data.error : null;
            yield put(GlobalAction.showGlobalError({ Message: errorMsg }));
            yield put(actionCreators.addCartItemFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.addCartItemFailure(err));
    }
}

const deleteRequestPayload = (data) => {
    return {
        shoppingCartId: !!data.cartId ? data.cartId : 0,
        deleteItems: [data.warrantyId],
    };
};

export function* deleteCartItem(action) {
    const payload = deleteRequestPayload(action.payload);
    const endpoint = myCartRoute.endpoints.deleteCartItem(payload);
    const state: RootState = yield select();
    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && response.data.isSuccess) {
            const { cartItems } = state.myCartInfo;
            const successMsg = getSuccessMsg(
                cartItems.find((item) => item.id === action.payload.warrantyId),
                "DELETE"
            );
            yield put(actionCreators.deleteCartItemSuccess({ data: response.data, successMsg }));
        } else {
            const errorMsg = response.data ? response.data.error : null;
            yield put(GlobalAction.showGlobalError({ Message: errorMsg }));
            yield put(actionCreators.deleteCartItemFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.deleteCartItemFailure(err));
    }
}

const beginTxRequestPayload = (payload) => {
    const { cartId, TotalShoppingCartCost, Currency, IsResubmit } = payload;
    let reqPayload: any = { ShoppingCartId: cartId };
    if (TotalShoppingCartCost && Currency) {
        reqPayload = { ...reqPayload, TotalShoppingCartCost, Currency };
    }
    if (IsResubmit) {
        reqPayload = { ...reqPayload, IsResubmit, TotalShoppingCartCost };
    }
    return reqPayload;
};

export function* beginTransaction(action) {
    const reqPayload = beginTxRequestPayload(action.payload);
    const endpoint = myCartRoute.endpoints.beginTransaction(reqPayload);
    let persistData: PersistData = getPersistData() || {};
    const state: RootState = yield select();
    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && (response.data.proceedToPayment || !!response.data.thankYouPageCartResponse)) {
            const { forminstanceTypeId } = state.dynamicFormInfo.layoutResponse;
            const { contractorSelection } = state.adminData;
            persistData = Object.assign(persistData, {
                isAlreadyPaymentInitiated: true,
                forminstanceTypeId,
                shoppingCartId: response.data.shoppingCartId,
                contractorId: contractorSelection && contractorSelection.contractorId,
                customerId: contractorSelection && contractorSelection.customerId,
            });
            delete persistData.warrantyId;
            setPersistData(persistData);
            if (!!response.data.thankYouPageCartResponse){
                const checkoutItems = parseResponseData(response.data.thankYouPageCartResponse);
                const { shoppingCartId } = response.data.thankYouPageCartResponse;
                yield put(thankyouAction.updateTransactionData({ warranties: checkoutItems, shoppingCartId }));
                yield put(actionCreators.resetShoppingCartDetails());
            }
            yield put(actionCreators.beginTransactionSuccess({ data: response.data }));
        } else {
            yield put(GlobalAction.showGlobalError({ Message: "BeginTransaction failure" }));
            yield put(actionCreators.beginTransactionFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.beginTransactionFailure(err));
    }
}

export function* displayReviewFormData(action) {
    const warrantyId = action.warrantyId;
    const reviewAndPayEndpoint = endpoints.thankYou.endpoints.reviewAndPay.endpoints.getReviewAndPay(warrantyId);
    try {
        const reviewAndPayResponse = yield call(fetchAPI, reviewAndPayEndpoint);
        if (reviewAndPayResponse && reviewAndPayResponse.data) {
            yield put(actionCreators.reviewWarrantyDataSuccess({ data: reviewAndPayResponse.data }));
        } else {
            yield put(actionCreators.resetReviewWarrantyData());
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
        yield put(actionCreators.resetReviewWarrantyData());
    }
}

export function* reSubmitShoppingCart(action) {
    const reqPayload = beginTxRequestPayload(action.payload);
    const endpoint = myCartRoute.endpoints.reSubmitShoppingCart(reqPayload);
    let persistData: PersistData = getPersistData() || {};
    const state: RootState = yield select();
    try {
        const response = yield call(fetchAPI, endpoint);
        if (response.data && (response.data.proceedToPayment || !!response.data.thankYouPageCartResponse)) {
            const { forminstanceTypeId } = state.dynamicFormInfo.layoutResponse;
            const { contractorSelection } = state.adminData;
            persistData = Object.assign(persistData, {
                isAlreadyPaymentInitiated: true,
                forminstanceTypeId,
                contractorId: contractorSelection && contractorSelection.contractorId,
                customerId: contractorSelection && contractorSelection.customerId,
            });
            setPersistData(persistData);
            if (!!response.data.thankYouPageCartResponse){
                const checkoutItems = parseResponseData(response.data.thankYouPageCartResponse);
                const { shoppingCartId } = response.data.thankYouPageCartResponse;
                yield put(thankyouAction.updateTransactionData({ warranties: checkoutItems, shoppingCartId }));
                yield put(actionCreators.resetShoppingCartDetails());
            }
            yield put(actionCreators.reSubmitCartSuccess({ data: response.data }));
        } else {
            yield put(GlobalAction.showGlobalError({ Message: "Re-Submit shopping cart failure" }));
            yield put(actionCreators.reSubmitCartFailure(new Error(JSON.stringify(response))));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError({ Message: err.message }));
        yield put(actionCreators.reSubmitCartFailure(err));
    }
}
export function* myCartWatcher() {
    yield takeEvery(actionTypes.GET_ACTIVE_SHOPPING_CART, getActiveShoppingCart);
    yield takeEvery(actionTypes.GET_CART_ITEMS, getCartItems);
    yield takeEvery(actionTypes.CREATE_SHOPPING_CART, createShoppingCart);
    yield takeEvery(actionTypes.ADD_CART_ITEM, addCartItem);
    yield takeEvery(actionTypes.DELETE_CART_ITEM, deleteCartItem);
    yield takeEvery(actionTypes.BEGIN_TRANSACTION, beginTransaction);
    yield takeEvery(actionTypes.REVIEW_WARRANTY_DATA, displayReviewFormData);
    yield takeEvery(actionTypes.RE_SUBMIT_CART, reSubmitShoppingCart);
}
